import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, BookOpen, Spinner } from "@phosphor-icons/react";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (polls.current >= MAX_POLLS) { setStatus("timeout"); return; }
      polls.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          clearCart();
          return;
        }
        if (res.data.status === "expired" || res.data.payment_status === "failed") {
          setStatus("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[75vh] grid place-items-center px-6" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white sticker rounded-3xl p-10 text-center max-w-md w-full">
        {status === "checking" && (
          <>
            <Spinner size={56} weight="duotone" className="mx-auto text-brand-sky animate-spin" />
            <h1 className="font-heading font-bold text-2xl text-ink mt-5">Confirming your purchase…</h1>
            <p className="font-body text-ink-soft mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle size={64} weight="fill" className="mx-auto text-brand-grass" />
            <h1 className="font-heading font-bold text-3xl text-ink mt-5">Hooray! 🎉</h1>
            <p className="font-body text-ink-soft mt-2">Your reading packages are now on your bookshelf.</p>
            <Button onClick={() => navigate("/library")} className="mt-6 w-full rounded-full bg-brand-grass hover:bg-brand-grass/90 text-white font-bold py-6 text-lg" data-testid="go-library-btn">
              <BookOpen size={20} weight="duotone" className="mr-2" /> Go to my bookshelf
            </Button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle size={64} weight="fill" className="mx-auto text-brand-coral" />
            <h1 className="font-heading font-bold text-2xl text-ink mt-5">Hmm, something went wrong</h1>
            <p className="font-body text-ink-soft mt-2">{status === "timeout" ? "Payment is taking longer than expected. Check your library shortly." : "We couldn't confirm your payment."}</p>
            <Link to="/cart"><Button className="mt-6 w-full rounded-full bg-brand-sky text-white font-bold py-6">Back to cart</Button></Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
